import React from 'react';
import {
  StyleSheet,
  View,
  Platform,
  TouchableWithoutFeedbackProps,
  TouchableNativeFeedback,
  TouchableOpacity,
  TextInput
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useTranslation } from 'react-i18next';

import Text from '../global/Text';
import { QuestionnaireQuestionProps } from './QuestionnaireQuestion';
import QuestionnaireDateTimePickerModal from './QuestionnaireDateTimePickerModal';
import { questionTextInvalid } from '../../utils/report';
import { checkDateFormat } from './utils/checkDateFormat';
import theme from '../../constants/theme';
import fontMaker from '../../utils/font';

export type QuestionnaireQuestionDateTimeProps = Pick<
  QuestionnaireQuestionProps,
  | 'question'
  | 'index'
  | 'currentConditionIndex'
  | 'language'
  | 'handleSelectChoice'
  | 'handleInputAnswer'
  | 'handleInputComment'
  | 'commentInputRef'
  | 'handleTextInputFocused'
  | 'handleTextInputBlurred'
>;

export type QuestionnaireQuestionDateTimePropsExtra = {
  styles: any;
};

const QuestionnaireQuestionDateTime = (
  props: QuestionnaireQuestionDateTimeProps & QuestionnaireQuestionDateTimePropsExtra
) => {
  const { t } = useTranslation(['questionnaire', 'common']);
  const [showPicker, setShowPicker] = React.useState<boolean>(false);

  // temporary fix to handle input flicker and moving cursor in RN 0.63.3
  // TODO: revert this once the bug is handled, or there is a permanent fix
  const [comment, setComment] = React.useState<string>(props.question.comment);
  React.useEffect(() => {
    setComment(props.question.comment);
  }, [props.question.answer]);

  const isNotApplicable = props.question.answer === 'not-applicable';

  // The date input turns red only when something invalid has been typed (empty
  // stays neutral). The comment box reddens on its own concern (too long).
  const answerStr = (props.question.answer || '').toString();
  const isDateInvalid = !isNotApplicable && answerStr.length > 0 && !checkDateFormat(answerStr);
  const isCommentTooLong = !!props.question.comment && props.question.comment.length >= 2500;
  const validationMessage = questionTextInvalid(props.question, t);

  const handleInputComment = (text: string) => {
    setComment(text);
    props.handleInputComment(props.index, text, props.currentConditionIndex!);
  };

  const handleInputAnswer = (text: string) => {
    props.handleInputAnswer(props.index, text, props.currentConditionIndex!);
  };

  return (
    <View>
      {!props.question.answerRequired ? (
        <View style={props.styles.choicesList}>
          <Touchable
            onPress={() => props.handleSelectChoice(props.index, 'not-applicable', props.currentConditionIndex!)}
            testID="datetime-na"
          >
            <View style={isNotApplicable ? [props.styles.choice, props.styles.choiceSelected] : props.styles.choice}>
              <View style={props.styles.choiceFlag}>
                <View style={props.styles.noFlag} />
              </View>
              <Text style={isNotApplicable ? [props.styles.choiceText, props.styles.choiceSelectedText] : props.styles.choiceText}>
                N/A
              </Text>
            </View>
          </Touchable>
        </View>
      ) : null}

      <View style={styles.dateRow}>
        <View
          style={[
            styles.dateInputContainer,
            isNotApplicable && props.styles.commentDisabled,
            {
              borderColor: isDateInvalid ? theme.colors.secondary : 'rgba(0, 0, 0, 0.05)'
            }
          ]}
        >
          <TextInput
            style={styles.dateInput}
            maxLength={20}
            editable={!isNotApplicable}
            onChangeText={handleInputAnswer}
            value={isNotApplicable ? '' : (props.question.answer || '').toString()}
            placeholder={
              isNotApplicable
                ? `${t('questionnaire:question.dateTime.notApplicable')}`
                : t('questionnaire:question.dateTime.placeholder')
            }
            testID="datetime-input"
          />
        </View>
        <TouchableOpacity
          style={[styles.calendarButton, isNotApplicable && styles.calendarButtonDisabled]}
          disabled={isNotApplicable}
          onPress={() => setShowPicker(true)}
          testID="datetime-open-picker"
        >
          <Icon name="calendar-month-outline" size={22} color={isNotApplicable ? '#c7c7c7' : theme.colors.primary} />
        </TouchableOpacity>
      </View>

      {/* Date validation message sits directly below the date input it refers to. */}
      {isDateInvalid ? (
        <View style={props.styles.validationContainer}>
          <Text style={props.styles.textValidationQuestion}> {validationMessage} </Text>
        </View>
      ) : null}

      <View
        style={[
          props.styles.openAnswerInputContainer,
          isNotApplicable && props.styles.commentDisabled,
          {
            borderColor: isCommentTooLong ? theme.colors.secondary : 'rgba(0, 0, 0, 0.05)'
          }
        ]}
      >
        <TextInput
          style={props.styles.openCommentInput}
          maxLength={2500}
          multiline={true}
          editable={!isNotApplicable}
          numberOfLines={4}
          onChangeText={handleInputComment}
          value={comment}
          placeholder={
            isNotApplicable
              ? `${t('questionnaire:question.dateTime.notApplicable')}`
              : `${t('questionnaire:question.dateTime.comments')}`
          }
          ref={props.commentInputRef}
          onFocus={() =>
            props.handleTextInputFocused(
              typeof props.currentConditionIndex! === 'number' ? props.currentConditionIndex! : props.index
            )
          }
          onBlur={() =>
            props.handleTextInputBlurred(
              typeof props.currentConditionIndex! === 'number' ? props.currentConditionIndex! : props.index
            )
          }
          testID="datetime-comment"
        />
      </View>

      {/* Comment-specific validation (e.g. too long) stays below the comment box. */}
      {!isDateInvalid && isCommentTooLong ? (
        <View style={props.styles.validationContainer}>
          <Text style={props.styles.textValidation}> {validationMessage} </Text>
        </View>
      ) : (
        <View style={props.styles.validationContainer}>
          <Text style={props.styles.textValidation}>&zwnj;</Text>
        </View>
      )}

      <QuestionnaireDateTimePickerModal
        visible={showPicker}
        value={isNotApplicable ? '' : (props.question.answer || '').toString()}
        onClose={() => setShowPicker(false)}
        onSave={(formatted: string) => handleInputAnswer(formatted)}
      />
    </View>
  );
};

export default QuestionnaireQuestionDateTime;

const styles = StyleSheet.create({
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 20,
    marginBottom: 15
  },
  dateInputContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 6,
    marginRight: 10
  },
  dateInput: {
    height: 39,
    ...fontMaker({ weight: 'Regular' }),
    color: '#535353',
    textAlignVertical: 'top',
    paddingVertical: 10,
    paddingHorizontal: 10
  },
  calendarButton: {
    width: 48,
    height: 41,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center'
  },
  calendarButtonDisabled: {
    backgroundColor: '#efeeed'
  }
});

const Touchable: React.ComponentClass<TouchableWithoutFeedbackProps> =
  Platform.OS === 'android' ? TouchableNativeFeedback : TouchableOpacity;
